import {Component, Input, Output, EventEmitter, ViewChild, ElementRef, Renderer2, OnInit, OnDestroy, AfterViewInit, HostListener, OnChanges, SimpleChanges} from '@angular/core';
import { CommonModule } from '@angular/common';
import {FormGroup, FormControl, Validators, ReactiveFormsModule} from '@angular/forms';
import {Observable, Subject, debounceTime, distinctUntilChanged, takeUntil} from 'rxjs';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './autocomplete.component.html'
})

export class AutocompleteComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @Input() label = '';
  @Input() placeholder = 'Empieza a escribir para buscar...';
  @Input() timeoutSeconds = 8;
  @Input() defaultValue: any;
  @Input() searchType: 'local' | 'remote' = 'local';
  @Input() items: any[] = [];
  @Input() remoteSearchFn?: (term: string) => Observable<any[]>;
  @Input() findByIdFn?: (id: any) => Observable<any>;
  @Input() labelKey = 'label';
  @Input() valueKey = 'value';

  @Input() disabled = false;
  @Input() disabledLabel = '';

  @Output() inputChange = new EventEmitter<string>();

  @Output() valueChange = new EventEmitter<any>();

  @ViewChild('input') input!: ElementRef<HTMLInputElement>;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;
  @ViewChild('container') container!: ElementRef;

  form = new FormGroup({
    search: new FormControl({value: '', disabled: this.disabled}, Validators.required)
  });

  filtered: any[] = [];
  hasSelected = false;
  offTimeOut = false;
  showNoResults = false;

  private timer: any;
  private destroy$ = new Subject<void>();
  private search$ = new Subject<string>();

  constructor(private renderer: Renderer2) {}

  ngOnInit(): void {
    this.startTimeout();

    if (this.searchType === 'remote') {
      this.search$
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(term => this.searchRemote(term));
    }
  }

  ngAfterViewInit(): void {
    if (this.disabled) {
      this.applyDisabledStyle();
    } else {
      this.applyEnabledStyle();
    }
    this.applyDefault();
  }

  private scheduleApplyDefault(): void {
    Promise.resolve().then(() => this.applyDefault());
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultValue'] || changes['items']) {
      setTimeout(() => {
        this.scheduleApplyDefault();
      }, 1000);
    }

    if (changes['disabled']) {
      const control = this.form.get('search');
      if (this.disabled) {
        control?.disable();
        if (this.subtitleLabel && this.inbox) {
          this.applyDisabledStyle();
        }
      } else {
        control?.enable();
        if (this.subtitleLabel && this.inbox) {
          this.applyEnabledStyle();
        }
      }
    }

    if (changes['defaultValue'] && changes['defaultValue'].currentValue === null) {
      this.form.get('search')?.setValue('', { emitEvent: false });
      this.hasSelected = false;
      this.offTimeOut = false;
      this.filtered = [];
      this.showNoResults = false;
      this.clearTimeout();
    }
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.timer);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.container.nativeElement.contains(event.target)) {
      this.filtered = [];
    }
  }

  onInput(): void {
    if (this.disabled) return;

    const value = this.form.value.search?.trim() || '';
    this.hasSelected = false;
    this.showNoResults = false;

    this.inputChange.emit(value);

    if (!value) {
      this.filtered = [];
      this.resetStyle();
      return;
    }

    this.activateLabel();
    this.applyEnabledStyle();

    if (this.searchType === 'local') {
      this.filtered = this.items.filter(item =>
        item[this.labelKey]?.toLowerCase().includes(value.toLowerCase())
      );

      const otraOption = this.items.find(item =>
        item[this.labelKey]?.toUpperCase() === 'OTRA' ||
        item[this.labelKey]?.toUpperCase() === 'OTRO'
      );

      if (otraOption && !this.filtered.some(item => item[this.valueKey] === otraOption[this.valueKey])) {
        this.filtered = [...this.filtered, otraOption];
      }

      if (this.filtered.length === 0 && value.length > 0 && this.label === '¿Cómo se llama la empresa para la que trabajas?') {
        this.filtered = [{ value: 1120, label: 'OTRA' }];
      }

      this.showNoResults = this.filtered.length === 0;
    } else {
      this.search$.next(value);
    }
  }

  private searchRemote(term: string): void {
    if (!this.remoteSearchFn) return;

    this.remoteSearchFn(term).subscribe({
      next: res => {
        this.filtered = res || [];
        this.showNoResults = this.filtered.length === 0;
      },
      error: () => {
        this.filtered = [];
        this.showNoResults = true;
      }
    });
  }

  select(item: any, emit = true): void {
    if (this.disabled) return;

    this.form.get('search')!.setValue(item[this.labelKey]);
    this.filtered = [];
    this.showNoResults = false;

    this.hasSelected = true;
    this.offTimeOut = false;

    this.applyEnabledStyle();

    if (emit) {
      this.valueChange.emit(item[this.valueKey]);
    }

    this.clearTimeout();
  }

  onBlur(): void {
    if (this.disabled) return;

    setTimeout(() => {
      if (!this.hasSelected) {
        this.offTimeOut = true;
        this.applyErrorStyle();
      }
    }, 300);
  }

  private startTimeout(): void {
    this.clearTimeout();
    this.timer = setTimeout(() => {
      if (!this.disabled && (!this.hasSelected || this.form.invalid)) {
        this.offTimeOut = true;
        this.applyErrorStyle();
      }
    }, this.timeoutSeconds * 1000);
  }

  private clearTimeout(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private activateLabel(): void {
    if (!this.disabled) {
      this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
    }
  }

  private resetStyle(): void {
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-error');
  }

  private applyEnabledStyle(): void {
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-error');
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');

    this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
    this.renderer.addClass(this.inbox.nativeElement, 'border-enabled');
  }

  private applyDisabledStyle(): void {
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

    this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
    this.renderer.addClass(this.inbox.nativeElement, 'border-disabled');
  }

  private applyErrorStyle(): void {
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');

    this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.addClass(this.inbox.nativeElement, 'border-error');
  }

  private applyDefault(): void {
    if (this.defaultValue == null || this.defaultValue === '' || this.defaultValue === undefined) {
        this.form.get('search')?.setValue('', { emitEvent: false });
        this.hasSelected = false;
        return;
    }

    if (this.searchType === 'remote' && this.findByIdFn) {
      this.findByIdFn(this.defaultValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          const item = res?.data ?? res;
          if (item) {
            this.select(item, false);
          }
        });
    }

    if (this.searchType === 'local' && this.items?.length) {
      const found = this.items.find(
        (item:any) => {
          return item[this.valueKey] === this.defaultValue;
        }
      );

      if (found) {
        this.select(found, false);
      }
    }
  }
}
